/**
 * Login-time sync of the sign-in address index (`users/{uid}.emails[]`).
 *
 * The index is what every email lookup matches on — friend add, platform
 * add-admin, gym member search, competition pickers. It is written ONLY by the
 * `syncAuthIdentities` Cloud Function (firestore.rules blocks client writes:
 * a client able to write it could insert someone else's address and receive
 * their friend requests and admin invites).
 *
 * So the client's job is just to notice when the stored index is stale and ask
 * the server to refresh it. The comparison happens locally and for free —
 * `user.providerData` is available for the signed-in user — so an account
 * that's already in sync costs nothing: no callable, no write. The call only
 * fires when something actually moved: a provider was linked or unlinked, an
 * email change completed, or the account pre-dates the index entirely.
 *
 * Best-effort throughout. A failure here must never block sign-in — the index
 * is a search convenience, and the next login retries it.
 */
import { buildEmailIndex, sameEmailIndex } from "./authIdentities";
import { callFunction } from "./callFunction";

/**
 * @param {?object} user - the Firebase User (needs `email` + `providerData`)
 * @param {?string[]} storedEmails - `users/{uid}.emails` as currently stored
 * @returns {Promise<boolean>} true when a server sync was performed
 */
export async function ensureAuthIdentities(user, storedEmails) {
  if (!user?.uid) return false;

  const expected = buildEmailIndex(user.providerData, user.email);
  if (sameEmailIndex(storedEmails, expected)) return false;

  try {
    await callFunction("syncAuthIdentities");
    return true;
  } catch {
    return false;
  }
}
